import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlSegmentGroup } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';

import { canAccessOrganization, getAccessibleOrganizationId } from '../domain/organization-access';
import { User } from '../domain/user-model';
import { AuthService } from '../services/auth-service';
import {
  organizationIdFromRef,
  organizationRouteSegment,
  resolveOrganizationRef,
  storeLastOrganization,
} from '@/shared/utils/organization-route';

function hasOrganizationOverride(group: UrlSegmentGroup): boolean {
  return (
    group.segments.some((segment) => Object.hasOwn(segment.parameters, 'org')) ||
    Object.values(group.children).some(hasOrganizationOverride)
  );
}

export const organizationGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Angular evaluates guards together, so do not request user data without a session.
  if (!localStorage.getItem('accessToken')) return router.parseUrl('/');

  // Matrix parameters can override paramMap values; the path identifies the organization.
  const organizationRef = route.url[0]?.path ?? '_org_';
  const accessPage = (reason: 'not-member' | 'forbidden' | 'error') =>
    router.createUrlTree(['/', organizationRef, 'acesso'], {
      queryParams: { reason, returnUrl: state.url },
    });
  const hasOrganizationContext = (user: User, organizationId: string) =>
    user.current_organization?.id === organizationId &&
    user.current_organization_id === organizationId;
  const activateOrganization = (user: User, organizationId: string) => {
    if (!canAccessOrganization(user, organizationId)) return accessPage('not-member');
    if (!hasOrganizationContext(user, organizationId)) return accessPage('error');

    storeLastOrganization(user.current_organization);
    return true;
  };

  if (hasOrganizationOverride(router.parseUrl(state.url).root)) {
    return accessPage('forbidden');
  }

  return authService.getMe().pipe(
    switchMap(({ data: user }) => {
      if (organizationRef === '_org_') {
        const accessibleOrganizationId = getAccessibleOrganizationId(user);
        const segment =
          organizationRouteSegment(resolveOrganizationRef(accessibleOrganizationId, user)) ??
          accessibleOrganizationId;
        return of(
          segment
            ? router.createUrlTree(['/', segment, 'dashboard'])
            : router.parseUrl('/dashboard/onboarding'),
        );
      }

      // The API may return a current organization that is not a membership.
      // Check access before switching, even when the current organization matches the URL.
      const organizationId = organizationIdFromRef(organizationRef, user);
      // Platform superusers can resolve nonmember slugs through the switch API.
      if (!organizationId && user.superuser) {
        return authService.switchOrganization(organizationRef).pipe(
          map(({ data: switchedUser }) => {
            const currentOrganization = switchedUser.current_organization;
            if (!currentOrganization || currentOrganization.slug !== organizationRef) {
              return accessPage('error');
            }
            return activateOrganization(switchedUser, currentOrganization.id);
          }),
        );
      }
      if (!organizationId || !canAccessOrganization(user, organizationId)) {
        return of(accessPage('not-member'));
      }

      if (hasOrganizationContext(user, organizationId)) {
        return of(activateOrganization(user, organizationId));
      }

      return authService
        .switchOrganization(organizationId)
        .pipe(map(({ data: switchedUser }) => activateOrganization(switchedUser, organizationId)));
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return of(router.parseUrl('/'));
      }

      return of(
        accessPage(
          error instanceof HttpErrorResponse && error.status === 403 ? 'forbidden' : 'error',
        ),
      );
    }),
  );
};
